import type { Feature, FeatureCollection, Geometry, Polygon } from 'geojson';

export type WhispIndicators = Record<string, boolean | null | undefined>;

export interface WhispDebugPart {
  partId: string;
  sourceFeature: number;
  sourcePart: number;
  grade: string;
  riskTimber?: string | null;
  indicators?: WhispIndicators | null;
  areaHa?: number | null;
  keyHa?: Record<string, number | null> | null;
  errorCode?: string | null;
}

export interface WhispDebugGroup {
  groupId: number;
  grade: string;
  partIds: string[];
  areaHa?: number | null;
  keyHa?: Record<string, number | null> | null;
  bboxLonLat?: number[] | null;
}

export interface WhispDebugOtherJob {
  env: string;
  partCount: number;
}

export interface WhispDebugCall {
  callId: string;
  status: string;
  polygonCount: number;
  byteSize: number;
  attempts: number;
  queuedAt?: string | null;
  submittedAt?: string | null;
  finishedAt?: string | null;
  errorCode?: string | null;
  ownPartIds: string[];
  otherJobs: WhispDebugOtherJob[];
}

export interface WhispDebugView {
  findingId: string;
  status: string;
  whispVersion?: string | null;
  checkedAt?: string | null;
  whispGeoHash?: string | null;
  whispDerivationVersion?: number | null;
  current: boolean;
  finding?: { grade: string; parts: WhispDebugPart[]; groups: WhispDebugGroup[]; whispVersion?: string | null } | null;
  raw?: FeatureCollection | null;
  calls?: WhispDebugCall[] | null;
}

export type TimberRisk = 'low' | 'high' | 'more_info_needed';

export interface TimberStep {
  step: number;
  rule: string;
  risk: TimberRisk;
}

export interface TreeNode {
  question: string;
  answer: boolean;
}

export interface TreePath {
  nodes: TreeNode[];
  risk: TimberRisk;
}

export type DebugRisk = TimberRisk | 'error';

export const WHISP_DEBUG_COLORS: Record<DebugRisk, string> = {
  low: '#43a047',
  more_info_needed: '#fb8c00',
  high: '#e53935',
  error: '#9e9e9e',
};

function flag(indicators: WhispIndicators, prefix: string): boolean {
  return Object.entries(indicators).some(([key, on]) => key.startsWith(`${prefix}_`) && on === true);
}

export function whispTimberStep(indicators: WhispIndicators): TimberStep {
  const has = (prefix: string) => flag(indicators, prefix);
  const naturalForest = has('Ind_05') || has('Ind_06');
  if (has('Ind_02')) return { step: 1, rule: 'Ind_02', risk: 'low' };
  if (has('Ind_07') && !has('Ind_10')) return { step: 2, rule: 'Ind_07 ∧ ¬Ind_10', risk: 'low' };
  if ((naturalForest || has('Ind_07')) && has('Ind_10'))
    return { step: 3, rule: '(Ind_05 ∨ Ind_06 ∨ Ind_07) ∧ Ind_10', risk: 'high' };
  if (naturalForest && has('Ind_08')) return { step: 4, rule: '(Ind_05 ∨ Ind_06) ∧ Ind_08', risk: 'high' };
  if (naturalForest && (has('Ind_09') || has('Ind_11')))
    return { step: 5, rule: '(Ind_05 ∨ Ind_06) ∧ (Ind_09 ∨ Ind_11)', risk: 'low' };
  if (naturalForest) return { step: 6, rule: 'Ind_05 ∨ Ind_06', risk: 'more_info_needed' };
  return { step: 7, rule: 'else', risk: 'low' };
}

export function claudiaTreePath(indicators: WhispIndicators): TreePath {
  const has = (prefix: string) => flag(indicators, prefix);
  const nodes: TreeNode[] = [];
  const ask = (question: string, answer: boolean) => {
    nodes.push({ question, answer });
    return answer;
  };
  if (ask('Agrar 2020 (Ind_02)', has('Ind_02'))) return { nodes, risk: 'low' };
  if (ask('Agrar 2024 (Ind_10)', has('Ind_10'))) return { nodes, risk: 'high' };
  if (ask('Plantage 2020 (Ind_07)', has('Ind_07'))) return { nodes, risk: 'low' };
  if (!ask('Naturwald 2020 (Ind_05 ∨ Ind_06)', has('Ind_05') || has('Ind_06'))) return { nodes, risk: 'low' };
  if (ask('Plantage 2024 (Ind_08)', has('Ind_08'))) return { nodes, risk: 'high' };
  if (ask('Regrowth/Logging (Ind_09 ∨ Ind_11)', has('Ind_09') || has('Ind_11'))) return { nodes, risk: 'low' };
  return { nodes, risk: 'more_info_needed' };
}

export function debugRiskOf(part: Pick<WhispDebugPart, 'riskTimber'>): DebugRisk {
  const risk = part.riskTimber;
  return risk === 'low' || risk === 'high' || risk === 'more_info_needed' ? risk : 'error';
}

export function rawRowsByPart(raw: FeatureCollection | null | undefined): Map<string, Feature> {
  const rows = new Map<string, Feature>();
  for (const feature of raw?.features ?? []) {
    const partId = feature.properties?.['external_id'];
    if (typeof partId === 'string') rows.set(partId, feature);
  }
  return rows;
}

export function groupOfPart(view: WhispDebugView): Map<string, number> {
  const groups = new Map<string, number>();
  for (const group of view.finding?.groups ?? []) group.partIds.forEach((partId) => groups.set(partId, group.groupId));
  return groups;
}

export function callOfPart(view: WhispDebugView): Map<string, number> {
  const calls = new Map<string, number>();
  (view.calls ?? []).forEach((call, index) => call.ownPartIds.forEach((partId) => calls.set(partId, index + 1)));
  return calls;
}

export function debugPartFeatures(view: WhispDebugView): Feature<Geometry>[] {
  const rows = rawRowsByPart(view.raw);
  const groups = groupOfPart(view);
  const calls = callOfPart(view);
  return (view.finding?.parts ?? []).flatMap((part) => {
    const geometry = rows.get(part.partId)?.geometry;
    if (!geometry) return [];
    const properties = {
      partId: part.partId,
      risk: debugRiskOf(part),
      groupId: groups.get(part.partId) ?? null,
      call: calls.get(part.partId) ?? null,
    };
    return [{ type: 'Feature', properties, geometry } as Feature<Geometry>];
  });
}

export function debugGroupOutlines(view: WhispDebugView): Feature<Polygon>[] {
  return (view.finding?.groups ?? []).flatMap((group) => {
    const [west, south, east, north] = group.bboxLonLat ?? [];
    if (north === undefined) return [];
    const ring = [
      [west, south],
      [east, south],
      [east, north],
      [west, north],
      [west, south],
    ];
    const geometry: Polygon = { type: 'Polygon', coordinates: [ring] };
    return [{ type: 'Feature', properties: { groupId: group.groupId, label: `G${group.groupId}` }, geometry }];
  });
}

export function isPointPart(
  siteGeo: FeatureCollection | null | undefined,
  part: Pick<WhispDebugPart, 'sourceFeature'>
): boolean {
  return siteGeo?.features?.[part.sourceFeature]?.geometry?.type === 'Point';
}
